/**
 * /tasks/:taskId/floorplan — floorplan-pinned punch screen (M8.3).
 *
 * Renders the project's design-package PDF in the browser via pdfjs
 * (no server-side rasterization) and overlays punch-item pins. The
 * supervisor selects a pending item from the sidebar, clicks anywhere
 * on the floorplan, and we persist normalized 0..1 coordinates + the
 * page_index against the punch_item. Already-pinned items render as
 * colored dots with hover labels and can be re-pinned or cleared.
 *
 * Why client-side PDF rendering: avoids a server-side native canvas
 * dependency. The PDF is fetched from /attachments/:id/file (already
 * exposed as a public stream by UUID). pdfjs-dist's worker is bundled
 * via Vite's `?url` import.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';
// Vite bundles the worker file and gives us a URL to point pdfjs at.
import pdfWorkerSrc from 'pdfjs-dist/build/pdf.worker.mjs?url';
import {
  AlertTriangle,
  ArrowLeft,
  ChevronLeft,
  ChevronRight,
  CircleSlash,
  Crosshair,
  FileWarning,
  Loader2,
  MapPin,
  ZoomIn,
  ZoomOut,
} from 'lucide-react';
import { toast } from 'sonner';
import type {
  Attachment,
  DesignPackagePage,
  FloorplanPin,
  PunchItem,
  PunchItemStatus,
} from '@vari-crew/schemas';
import { useTask } from '@/api/tasks';
import { useProject } from '@/api/projects';
import { usePunchItems, useUpdatePunchItem } from '@/api/punch-items';
import { useDesignPackage } from '@/api/design-package';
import { attachmentFileUrl } from '@/api/attachments';
import { Button } from '@/components/ui/Button';
import { Card, CardContent } from '@/components/ui/Card';
import { cn } from '@/lib/cn';

// One-time pdfjs worker registration.
pdfjs.GlobalWorkerOptions.workerSrc = pdfWorkerSrc;

// Saturated fill colors that read clearly on top of an off-white floorplan
// PDF — the soft `bg-status-*` tokens are tuned for pill backgrounds on a
// white card, not for 20px dots over a busy line drawing.
const STATUS_DOT_CLASS: Record<PunchItemStatus, string> = {
  open: 'bg-amber-500 border-white',
  assigned: 'bg-sky-600 border-white',
  in_progress: 'bg-orange-600 border-white',
  closed: 'bg-emerald-600 border-white',
};
const STATUS_BADGE_CLASS: Record<PunchItemStatus, string> = {
  open: 'bg-amber-100 text-amber-900',
  assigned: 'bg-sky-100 text-sky-900',
  in_progress: 'bg-orange-100 text-orange-900',
  closed: 'bg-emerald-100 text-emerald-900',
};

const STATUS_LABEL: Record<PunchItemStatus, string> = {
  open: 'Open',
  assigned: 'Assigned',
  in_progress: 'In progress',
  closed: 'Closed',
};

/** Pages that make sense as a pinning canvas (floorplans, not 3D / schedules). */
function isFloorplanPage(p: DesignPackagePage): boolean {
  return (
    p.part === 'as_built_furniture_plan' ||
    p.part === 'proposed_furniture_plan'
  );
}

export function FloorplanCanvas() {
  const { taskId } = useParams<{ taskId: string }>();
  const navigate = useNavigate();
  const { data: task } = useTask(taskId);
  const { data: project } = useProject(task?.project_id);
  const { data: designPackage, isLoading: dpLoading } = useDesignPackage(
    task?.project_id,
  );
  const { data: items, isLoading: itemsLoading } = usePunchItems(taskId);
  const updateItem = useUpdatePunchItem(taskId ?? '');

  // Default to the R1.1 (as_built_furniture_plan) page if it exists,
  // else the first floorplan-style page, else page 0.
  const pageIndex = useDefaultPageIndex(designPackage);
  const [currentPage, setCurrentPage] = useState<number | null>(null);
  useEffect(() => {
    if (currentPage === null && pageIndex !== null) setCurrentPage(pageIndex);
  }, [currentPage, pageIndex]);

  const [pinningItemId, setPinningItemId] = useState<string | null>(null);
  // Default zoom of 0.6 fits a 1224pt-wide landscape design page into a
  // typical 1100px-ish content column without overflowing immediately.
  const [zoom, setZoom] = useState(0.6);

  // Stable PDF URL — pdfjs can't send auth headers, so this must be the
  // signed file_url (the bare route now requires credentials). Deps lock to
  // the primitive id: React Query hands back a new object (and a fresh
  // signature) on every refetch, and reloading the whole PDF for a sig churn
  // would flash the canvas.
  const designPackageId = designPackage?.id;
  // eslint-disable-next-line react-hooks/exhaustive-deps -- sig churn must not reload the PDF
  const fileUrl = useMemo(
    () => (designPackage ? attachmentFileUrl(designPackage) : ''),
    [designPackageId],
  );

  const isLoading = dpLoading || itemsLoading;

  // Items visible on the current page, plus all items grouped for the sidebar
  const pageItems = useMemo(() => {
    if (!items || currentPage === null) return [];
    return items.data.filter(
      (i) => i.floorplan_pin && i.floorplan_pin.page_index === currentPage,
    );
  }, [items, currentPage]);

  const handleCanvasClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (!pinningItemId || currentPage === null) return;
      const rect = e.currentTarget.getBoundingClientRect();
      const x = (e.clientX - rect.left) / rect.width;
      const y = (e.clientY - rect.top) / rect.height;
      if (x < 0 || x > 1 || y < 0 || y > 1) return;
      const pin: FloorplanPin = {
        x: Math.round(x * 1000) / 1000,
        y: Math.round(y * 1000) / 1000,
        page_index: currentPage,
      };
      updateItem.mutate(
        { id: pinningItemId, body: { floorplan_pin: pin } },
        {
          onSuccess: () => {
            toast.success('Pin placed');
            setPinningItemId(null);
          },
          onError: (err) => toast.error(`Failed: ${err.message}`),
        },
      );
    },
    [pinningItemId, currentPage, updateItem],
  );

  const handleClearPin = useCallback(
    (itemId: string) => {
      updateItem.mutate(
        { id: itemId, body: { floorplan_pin: null } },
        {
          onSuccess: () => toast.success('Pin cleared'),
          onError: (err) => toast.error(`Failed: ${err.message}`),
        },
      );
    },
    [updateItem],
  );

  if (isLoading) {
    return (
      <div className="p-6 flex items-center gap-2 text-ink-faint">
        <Loader2 className="w-5 h-5 animate-spin" /> Loading floorplan…
      </div>
    );
  }

  if (!designPackage) {
    return (
      <EmptyState
        onBack={() => navigate(-1)}
        projectTitle={project?.title}
        taskTitle={task?.title}
        taskId={taskId}
      />
    );
  }

  const pages = designPackage.pages ?? [];
  const floorplanPages = pages.filter(isFloorplanPage);
  const currentPageInfo =
    pages.find((p) => p.page_index === currentPage) ?? null;

  return (
    <div className="h-[calc(100vh-3rem)] flex flex-col">
      {/* Header */}
      <div className="px-4 py-3 border-b border-line flex items-center gap-3 bg-bg shrink-0">
        <Button variant="ghost" size="icon" onClick={() => navigate(-1)} aria-label="Back">
          <ArrowLeft className="w-4 h-4" />
        </Button>
        <div className="flex-1 min-w-0">
          <div className="text-xs text-ink-faint truncate">
            {project ? (
              <Link to={`/projects/${project.id}`} className="hover:underline">
                {project.title}
              </Link>
            ) : (
              'Project'
            )}{' '}
            ·{' '}
            <Link
              to={`/tasks/${taskId}/punch`}
              className="hover:underline font-mono"
            >
              {task?.title ?? 'Punch list'}
            </Link>
          </div>
          <h1 className="text-lg font-semibold flex items-center gap-2">
            Floorplan{' '}
            {currentPageInfo?.page_label && (
              <span className="text-xs font-mono px-1.5 py-0.5 rounded border border-line text-ink-dim">
                {currentPageInfo.page_label}
              </span>
            )}
          </h1>
        </div>
        {/* Page selector */}
        {floorplanPages.length > 1 && (
          <div className="flex items-center gap-1">
            {floorplanPages.map((p) => (
              <button
                key={p.page_index}
                onClick={() => setCurrentPage(p.page_index)}
                className={cn(
                  'px-2 py-1 rounded text-xs border transition-colors',
                  currentPage === p.page_index
                    ? 'border-ink bg-ink text-bg'
                    : 'border-line text-ink-dim hover:bg-fill-soft',
                )}
                title={p.part.replaceAll('_', ' ')}
              >
                {p.page_label ?? `Page ${p.page_index + 1}`}
              </button>
            ))}
          </div>
        )}
        {/* Zoom */}
        <div className="flex items-center gap-1 border border-line rounded">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setZoom((z) => Math.max(0.5, z - 0.25))}
            disabled={zoom <= 0.5}
            aria-label="Zoom out"
          >
            <ZoomOut className="w-3.5 h-3.5" />
          </Button>
          <span className="text-xs font-mono tabular-nums px-1 text-ink-dim">
            {Math.round(zoom * 100)}%
          </span>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setZoom((z) => Math.min(2, z + 0.25))}
            disabled={zoom >= 2}
            aria-label="Zoom in"
          >
            <ZoomIn className="w-3.5 h-3.5" />
          </Button>
        </div>
      </div>

      <div className="flex-1 flex min-h-0">
        {/* Sidebar: punch items */}
        <aside className="w-80 border-r border-line flex flex-col bg-bg shrink-0">
          <div className="px-3 py-2 border-b border-line text-xs uppercase tracking-wide text-ink-faint">
            Punch items
          </div>
          <div className="flex-1 overflow-y-auto">
            {(items?.data ?? []).length === 0 ? (
              <div className="p-4 text-sm text-ink-dim">No punch items.</div>
            ) : (
              <ul className="divide-y divide-line">
                {items?.data.map((item) => (
                  <SidebarItem
                    key={item.id}
                    item={item}
                    isPinning={pinningItemId === item.id}
                    onStartPinning={() => setPinningItemId(item.id)}
                    onCancelPinning={() => setPinningItemId(null)}
                    onClearPin={() => handleClearPin(item.id)}
                    onJumpToPage={(idx) => setCurrentPage(idx)}
                  />
                ))}
              </ul>
            )}
          </div>
          {pinningItemId && (
            <div className="border-t border-line p-3 bg-fill-soft text-xs flex items-start gap-2">
              <Crosshair className="w-3.5 h-3.5 mt-0.5 text-status-onsite-fg shrink-0" />
              <div className="flex-1">
                <div className="font-medium">Pinning mode</div>
                <div className="text-ink-dim">
                  Click anywhere on the floorplan to drop the pin.
                </div>
              </div>
              <Button
                size="sm"
                variant="outline"
                onClick={() => setPinningItemId(null)}
              >
                Cancel
              </Button>
            </div>
          )}
        </aside>

        {/* Canvas — overflow:auto allows pan when the PDF is wider/taller
            than the viewport. `min-w-max` on the inner wrapper keeps the
            flex container from clipping the page when it overflows. */}
        <main className="flex-1 overflow-auto bg-fill-soft">
          {currentPage !== null && (
            <div className="p-6 min-w-max">
              <FloorplanPdf
                fileUrl={fileUrl}
                pageIndex={currentPage}
                zoom={zoom}
                pins={pageItems}
                pinningMode={!!pinningItemId}
                onCanvasClick={handleCanvasClick}
              />
            </div>
          )}
        </main>
      </div>
    </div>
  );
}

// ----------------------------------------------------------------------------
// PDF page + pin overlay
// ----------------------------------------------------------------------------
function FloorplanPdf({
  fileUrl,
  pageIndex,
  zoom,
  pins,
  pinningMode,
  onCanvasClick,
}: {
  fileUrl: string;
  pageIndex: number;
  zoom: number;
  pins: PunchItem[];
  pinningMode: boolean;
  onCanvasClick: (e: React.MouseEvent<HTMLDivElement>) => void;
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [docError, setDocError] = useState<string | null>(null);

  if (docError) {
    return (
      <Card className="max-w-md">
        <CardContent className="p-6 flex items-start gap-3">
          <FileWarning className="w-5 h-5 text-status-blocked-fg shrink-0 mt-0.5" />
          <div>
            <div className="font-semibold">Could not render the floorplan</div>
            <p className="text-sm text-ink-dim mt-1">{docError}</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div
      ref={containerRef}
      className="relative shadow-lg bg-bg inline-block"
      onClick={onCanvasClick}
      style={{ cursor: pinningMode ? 'crosshair' : 'default' }}
    >
      <Document
        // Pass the URL as a string — react-pdf will warn about object
        // identity churn if we wrap it in `{ url }` even with useMemo.
        file={fileUrl}
        loading={
          <div className="p-12 flex items-center gap-2 text-ink-faint">
            <Loader2 className="w-4 h-4 animate-spin" /> Rendering PDF…
          </div>
        }
        error={
          <div className="p-12 text-sm text-status-blocked-fg">
            Failed to load PDF.
          </div>
        }
        onLoadError={(err) => setDocError(err.message)}
      >
        <Page
          pageIndex={pageIndex}
          scale={zoom * 1.5}
          renderTextLayer={false}
          renderAnnotationLayer={false}
          loading={
            <div className="p-12 flex items-center gap-2 text-ink-faint">
              <Loader2 className="w-4 h-4 animate-spin" /> Rendering page…
            </div>
          }
        />
      </Document>

      {/* Pin overlay — absolute-positioned, doesn't intercept clicks except on dots */}
      <div className="absolute inset-0 pointer-events-none">
        {pins.map((item) => {
          if (!item.floorplan_pin) return null;
          return (
            <Pin
              key={item.id}
              item={item}
              x={item.floorplan_pin.x}
              y={item.floorplan_pin.y}
            />
          );
        })}
      </div>
    </div>
  );
}

function Pin({ item, x, y }: { item: PunchItem; x: number; y: number }) {
  const isClosed = item.status === 'closed';
  return (
    <div
      className="absolute -translate-x-1/2 -translate-y-1/2 group pointer-events-auto"
      style={{ left: `${x * 100}%`, top: `${y * 100}%` }}
    >
      <div
        className={cn(
          'w-5 h-5 rounded-full border-2 shadow-lg transition-transform group-hover:scale-125',
          STATUS_DOT_CLASS[item.status],
          isClosed && 'opacity-70',
          item.blocks_recap && !isClosed && 'ring-2 ring-red-500 ring-offset-2',
        )}
      />
      {/* Hover label */}
      <div className="absolute left-1/2 -translate-x-1/2 -top-2 -translate-y-full whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity z-10 pointer-events-none">
        <div className="bg-ink text-bg text-xs rounded px-2 py-1 shadow-lg max-w-xs">
          <div className="font-medium truncate">{item.title}</div>
          {item.zone_tag && (
            <div className="font-mono text-[10px] opacity-80">
              {item.zone_tag}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ----------------------------------------------------------------------------
// Sidebar item
// ----------------------------------------------------------------------------
function SidebarItem({
  item,
  isPinning,
  onStartPinning,
  onCancelPinning,
  onClearPin,
  onJumpToPage,
}: {
  item: PunchItem;
  isPinning: boolean;
  onStartPinning: () => void;
  onCancelPinning: () => void;
  onClearPin: () => void;
  onJumpToPage: (pageIndex: number) => void;
}) {
  const hasPin = !!item.floorplan_pin;
  const isClosed = item.status === 'closed';
  return (
    <li
      className={cn(
        'p-3 text-sm',
        isPinning && 'bg-status-onsite/10',
        isClosed && 'opacity-60',
      )}
    >
      <div className="flex items-start gap-2">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 flex-wrap">
            <span
              className={cn(
                'inline-flex items-center rounded-full px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide',
                STATUS_BADGE_CLASS[item.status],
              )}
            >
              {STATUS_LABEL[item.status]}
            </span>
            {hasPin && (
              <button
                onClick={() => onJumpToPage(item.floorplan_pin!.page_index)}
                className="inline-flex items-center gap-1 text-[10px] text-status-onsite-fg hover:underline"
                title="Jump to this pin"
              >
                <MapPin className="w-2.5 h-2.5" />
                pinned
              </button>
            )}
            {item.blocks_recap && !isClosed && (
              <span className="inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide bg-status-blocked text-status-blocked-fg">
                <AlertTriangle className="w-2 h-2" /> blocks recap
              </span>
            )}
          </div>
          <div
            className={cn(
              'mt-1 font-medium leading-tight',
              isClosed && 'line-through',
            )}
          >
            {item.title}
          </div>
          {item.zone_tag && (
            <div className="font-mono text-[10px] text-ink-faint mt-0.5">
              {item.zone_tag}
            </div>
          )}
        </div>
      </div>
      <div className="mt-2 flex gap-1">
        {isPinning ? (
          <Button size="sm" variant="outline" onClick={onCancelPinning} className="flex-1">
            Cancel
          </Button>
        ) : (
          <Button
            size="sm"
            variant={hasPin ? 'outline' : 'default'}
            onClick={onStartPinning}
            className="flex-1"
            disabled={isClosed}
          >
            <Crosshair className="w-3 h-3" />
            {hasPin ? 'Re-pin' : 'Place pin'}
          </Button>
        )}
        {hasPin && !isPinning && (
          <Button
            size="sm"
            variant="ghost"
            onClick={onClearPin}
            title="Clear pin"
          >
            <CircleSlash className="w-3 h-3" />
          </Button>
        )}
      </div>
    </li>
  );
}

// ----------------------------------------------------------------------------
// Empty state when no design package has been uploaded
// ----------------------------------------------------------------------------
function EmptyState({
  onBack,
  projectTitle,
  taskTitle,
  taskId,
}: {
  onBack: () => void;
  projectTitle: string | undefined;
  taskTitle: string | undefined;
  taskId: string | undefined;
}) {
  return (
    <div className="p-6 max-w-2xl mx-auto space-y-4">
      <header className="flex items-start gap-3">
        <Button variant="ghost" size="icon" onClick={onBack} aria-label="Back">
          <ArrowLeft className="w-4 h-4" />
        </Button>
        <div className="flex-1">
          <div className="text-xs text-ink-faint">
            {projectTitle ?? 'Project'} ·{' '}
            <span className="font-mono">{taskTitle ?? 'Punch list'}</span>
          </div>
          <h1 className="text-2xl font-semibold">Floorplan</h1>
        </div>
      </header>
      <Card>
        <CardContent className="py-12 text-center space-y-3">
          <FileWarning className="w-10 h-10 mx-auto text-ink-faint" />
          <div className="font-semibold">No floorplan uploaded yet</div>
          <p className="text-sm text-ink-dim max-w-md mx-auto">
            Upload the project's design package (PDF) to this project to enable
            floorplan pinning. Until then, use zone tags (e.g. <code>L3 · WS3</code>)
            on punch items.
          </p>
          {taskId && (
            <div className="pt-2">
              <Button variant="outline" asChild>
                <Link to={`/tasks/${taskId}/punch`}>
                  <ChevronLeft className="w-3.5 h-3.5" />
                  Back to punch list
                </Link>
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// ----------------------------------------------------------------------------
// Helpers
// ----------------------------------------------------------------------------
function useDefaultPageIndex(
  designPackage: Attachment | null | undefined,
): number | null {
  return useMemo(() => {
    if (!designPackage) return null;
    const pages = designPackage.pages ?? [];
    const asBuilt = pages.find((p) => p.part === 'as_built_furniture_plan');
    if (asBuilt) return asBuilt.page_index;
    const proposed = pages.find((p) => p.part === 'proposed_furniture_plan');
    if (proposed) return proposed.page_index;
    const anyFloorplan = pages.find(isFloorplanPage);
    if (anyFloorplan) return anyFloorplan.page_index;
    return pages[0]?.page_index ?? 0;
  }, [designPackage]);
}

// Silence eslint about unused icons in conditional branches.
void ChevronRight;
